import { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';

export interface ConfirmRequest {
  title: string;
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  onConfirm: () => void | Promise<void>;
}

// IXAM: hook per richiedere conferme stile Grenoble da qualsiasi pagina,
// in sostituzione di window.confirm. Non si limita al delete: qualunque
// azione "pesante" la usa con destructive: true per il bottone rosso.
export function useConfirm() {
  const [request, setRequest] = useState<ConfirmRequest | null>(null);
  return {
    request,
    ask: (req: ConfirmRequest) => setRequest(req),
    dismiss: () => setRequest(null),
  };
}

export function ConfirmDialog({
  request,
  onClose,
}: {
  request: ConfirmRequest | null;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);

  // IXAM: chiude il dialog su Esc per parità con i pattern modali standard.
  useEffect(() => {
    if (!request) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape' && !busy) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [request, busy, onClose]);

  if (!request) return null;

  async function confirm(): Promise<void> {
    if (!request) return;
    setBusy(true);
    try {
      await request.onConfirm();
      onClose();
    } finally {
      setBusy(false);
    }
  }

  const destructive = request.destructive ?? false;

  return (
    <div
      className="fixed inset-0 bg-ink/40 backdrop-blur-sm flex items-center justify-center p-4 z-[60]"
      onClick={() => !busy && onClose()}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-paper-card border border-line-soft rounded-2xl shadow-soft p-6 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-4">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
              destructive ? 'bg-sev-critical-soft text-sev-critical' : 'bg-accent/10 text-accent'
            }`}
          >
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-lg text-ink leading-tight">{request.title}</h3>
            {request.body && (
              <p className="text-sm text-ink-muted mt-1.5 leading-relaxed">{request.body}</p>
            )}
          </div>
        </div>
        <div className="flex gap-2 pt-5 mt-5 border-t border-line-soft">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="ixam-btn-ghost flex-1 justify-center"
          >
            {request.cancelLabel ?? 'Annulla'}
          </button>
          <button
            type="button"
            onClick={() => void confirm()}
            disabled={busy}
            className={`flex-1 justify-center inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              destructive
                ? 'bg-sev-critical hover:bg-sev-critical/90'
                : 'bg-accent hover:bg-accent-hover'
            }`}
          >
            {busy ? 'Attendere…' : (request.confirmLabel ?? 'Conferma')}
          </button>
        </div>
      </div>
    </div>
  );
}
